import { ManagedFragmentDto, ModuleDto } from './dtos';
import { fieldWeight } from './field-weight';

export interface ModuleRevision {
    id: string;
    object_type: 'managed_module' | 'unmanaged_module';
    schema_version: number;
    created_at: string;
    created_by_name: string | null;
}
export interface ModuleHistory { data: ModuleRevision[]; has_more: boolean }
export interface ModuleRevisionSnapshot { schema_version: number; module: ModuleDto }

/** Object property order and transport-only JSON columns are not content changes. */
function canonical(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(canonical);
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b)).map(([key, child]) => [key, canonical(child)]));
    return value;
}

function fragmentContent(fragment: ManagedFragmentDto) {
    return {
        title: fragment.title ?? '', html: fragment.html ?? '', instructions: fragment.instructions ?? '',
        answers_explanation: fragment.answers_explanation ?? '', aiHint: fragment.aiHint ?? '',
        hidden: !!fragment.hidden, fillOnce: !!fragment.fillOnce,
        formFields: [...(fragment.formFields ?? [])].sort((a, b) => a.key.localeCompare(b.key)).map(field => ({
            key: field.key, type: field.type, label: field.label ?? '', required: !!field.required,
            is_label_hidden: !!field.is_label_hidden, placeholder: field.placeholder ?? '', maxLength: field.maxLength ?? null,
            options: field.options ?? [], defaultValue: field.defaultValue ?? '', expectedValue: field.expectedValue ?? '',
            tooltip: field.tooltip ?? '', aiHint: field.aiHint ?? '', weight: fieldWeight(field), attributes: field.attributes ?? {},
        })),
        actions: [...(fragment.actions ?? [])].sort((a, b) => a.order - b.order || a.key.localeCompare(b.key)).map(action => ({
            key: action.key, label: action.label ?? '', type: action.type, to: action.to ?? '', payload: action.payload ?? null,
        })),
    };
}

export function fragmentContentsDiffer(left?: ManagedFragmentDto, right?: ManagedFragmentDto): boolean {
    if (!left || !right) return left !== right;
    return JSON.stringify(canonical(fragmentContent(left))) !== JSON.stringify(canonical(fragmentContent(right)));
}

/** Compare restorable content, ignoring save metadata and transport-only columns. */
export function moduleContentsDiffer(left: ModuleDto, right: ModuleDto): boolean {
    const content = (module: ModuleDto) => ({
        name: module.name ?? '', description: module.description ?? '', content: module.content ?? '',
        instructions: module.instructions ?? '', estimated_minutes: module.estimated_minutes ?? null,
        is_managed: !!module.is_managed, finalQuizFragmentKey: module.finalQuizFragmentKey ?? null,
        theme: module.theme ?? [],
        prerequisites: [...(module.prerequisites ?? [])].sort((a, b) => a.module_key.localeCompare(b.module_key))
            .map(item => ({ module_key: item.module_key, is_optional: !!item.is_optional })),
        fragments: [...(module.fragments ?? [])].sort((a, b) => a.order - b.order)
            .map(fragment => ({ key: fragment.key, ...fragmentContent(fragment) })),
    });
    return JSON.stringify(canonical(content(left))) !== JSON.stringify(canonical(content(right)));
}

/** Keep identity, placement and concurrency metadata attached to the current object. */
export function restoredModule(snapshot: ModuleDto, current: ModuleDto): ModuleDto {
    if (snapshot.module_key !== current.module_key || !!snapshot.is_managed !== !!current.is_managed)
        throw new Error('This revision belongs to a different module or editor type.');
    return { ...structuredClone(snapshot), module_key: current.module_key, folder_id: current.folder_id,
        created_at: current.created_at, updated_at: current.updated_at };
}

/** Restore one fragment without replacing any other part of the current draft. */
export function restoredFragment(snapshot: ModuleDto, current: ModuleDto, key: string): ModuleDto {
    if (snapshot.module_key !== current.module_key || !snapshot.is_managed || !current.is_managed)
        throw new Error('This fragment belongs to a different module or editor type.');
    const source = snapshot.fragments?.find(fragment => fragment.key === key);
    if (!source) throw new Error('This fragment does not exist in the selected revision.');
    const fragments = current.fragments ?? [];
    const existing = fragments.find(fragment => fragment.key === key);
    const fieldKeys = new Set((source.formFields ?? []).map(field => field.key));
    const actionKeys = new Set((source.actions ?? []).map(action => action.key));
    if (fragments.some(fragment => fragment.key !== key && (
        fragment.formFields?.some(field => fieldKeys.has(field.key)) || fragment.actions?.some(action => actionKeys.has(action.key)))))
        throw new Error('Some fields or actions from this revision are now used by another fragment. Resolve those conflicts before restoring this fragment.');
    const restored = { ...structuredClone(source), order: existing?.order ?? fragments.reduce((last, fragment) => Math.max(last, fragment.order), -1) + 1 };
    return { ...current, fragments: existing ? fragments.map(fragment => fragment.key === key ? restored : fragment)
        : [...fragments, restored] };
}
